import { Component, ElementRef, OnDestroy, OnInit, ViewChild, Inject, PLATFORM_ID, ChangeDetectorRef, HostListener } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@environments/environment';
import { HoliThemeService } from '@app/core/services/holi-theme.service';

@Component({
  selector: 'app-holi-particles',
  template: `
    <canvas #canvas class="holi-particles-canvas" [style.display]="isVisible ? 'block' : 'none'"></canvas>
  `,
  styles: [`
    .holi-particles-canvas {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      opacity: 0.9;
    }
  `]
})
export class HoliParticlesComponent implements OnInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvas: ElementRef<HTMLCanvasElement>;
  private ctx: CanvasRenderingContext2D;
  private particles: GulalParticle[] = [];
  private animationFrame: number;
  private isBrowser: boolean;
  isVisible = true;

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private holiService: HoliThemeService,
    private cdr: ChangeDetectorRef
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.initCanvas();
      this.createParticles();
      this.animate();
      window.addEventListener('resize', this.onResize.bind(this));

      this.holiService.particlesVisible$.subscribe(visible => {
        this.isVisible = visible;
        this.cdr.detectChanges();
      });
    }
  }

  ngOnDestroy() {
    if (this.isBrowser) {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }
      window.removeEventListener('resize', this.onResize.bind(this));
    }
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(e: MouseEvent) {
    if (!this.isVisible || !this.isBrowser) return;
    // Create subtle trail
    this.createTrail(e.clientX, e.clientY);
  }

  @HostListener('window:click', ['$event'])
  onClick(e: MouseEvent) {
    if (!this.isVisible || !this.isBrowser) return;
    // Create blast
    this.createBlast(e.clientX, e.clientY);
  }

  private onResize() {
    if (this.canvas && this.canvas.nativeElement) {
      this.canvas.nativeElement.width = window.innerWidth;
      this.canvas.nativeElement.height = window.innerHeight;
    }
  }

  private initCanvas() {
    const canvasEl = this.canvas.nativeElement;
    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;
    this.ctx = ctx;
    canvasEl.width = window.innerWidth;
    canvasEl.height = window.innerHeight;
  }

  private createParticles() {
    const count = (environment.holi && environment.holi.particleCount) || 50;
    // Reduce count on mobile for performance
    const actualCount = window.innerWidth < 768 ? Math.floor(count * 0.4) : count;

    for (let i = 0; i < actualCount; i++) {
        this.particles.push(new GulalParticle(
            Math.random() * window.innerWidth,
            Math.random() * window.innerHeight,
            'rain'
        ));
    }
  }

  private createTrail(x: number, y: number) {
      // Small dust trail
      const particle = new GulalParticle(x, y, 'trail');
      this.particles.push(particle);
  }

  private createBlast(x: number, y: number) {
      // Explosion of 20 particles
      for (let i = 0; i < 20; i++) {
          this.particles.push(new GulalParticle(x, y, 'blast'));
      }
  }

  private animate() {
    if (!this.ctx || !this.canvas) return;

    this.ctx.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);

    // Update and Draw
    this.particles.forEach(particle => {
      particle.update();
      particle.draw(this.ctx);
    });

    // Remove dead particles (blast/trail that faded out)
    this.particles = this.particles.filter(p => p.isAlive);

    this.animationFrame = requestAnimationFrame(() => this.animate());
  }
}

class GulalParticle {
  x: number;
  y: number;
  size: number;
  speedY: number;
  speedX: number;
  wobble: number;
  wobbleSpeed: number;
  opacity: number;
  color: string;
  type: 'rain' | 'blast' | 'trail';
  isAlive: boolean = true;
  decay: number;

  private static readonly PALETTE = [
    '#FF6B00', // Saffron
    '#E91E8C', // Deep Pink  
    '#00B8FF', // Electric Blue
    '#4CAF50', // Lime Green
    '#9C27B0', // Violet
    '#FFD700', // Golden Yellow
    '#FF4081', // Hot Pink
    '#00BCD4', // Cyan
    '#8BC34A', // Light Green
    '#FF9800', // Orange
  ];

  constructor(x: number, y: number, type: 'rain' | 'blast' | 'trail' = 'rain') {
    this.x = x;
    this.y = y;
    this.type = type;
    this.color = GulalParticle.PALETTE[Math.floor(Math.random() * GulalParticle.PALETTE.length)];

    if (type === 'rain') {
        const isSpray = Math.random() > 0.8;
        if (isSpray) {
            this.size = Math.random() * 2 + 1; // Spray: 1-3px
            this.speedY = Math.random() * 2 + 2; 
            this.opacity = Math.random() * 0.6 + 0.4;
        } else {
            this.size = Math.random() * 6 + 2; // Blob: 2-8px (was 5-20px)
            this.speedY = Math.random() * 1 + 0.5; 
            this.opacity = Math.random() * 0.4 + 0.2; 
        }
        this.speedX = (Math.random() - 0.5) * 1;
        this.wobble = Math.random() * Math.PI * 2;
        this.wobbleSpeed = Math.random() * 0.05 + 0.01;
        this.decay = 0; // Doesn't decay, recycles start

    } else if (type === 'blast') {
        // Explosive radial movement
        this.size = Math.random() * 5 + 2; // Blast: 2-7px (was 4-12px)
        const angle = Math.random() * Math.PI * 2;
        const velocity = Math.random() * 6 + 2;
        this.speedX = Math.cos(angle) * velocity;
        this.speedY = Math.sin(angle) * velocity;
        this.opacity = 1;
        this.decay = Math.random() * 0.02 + 0.015; // Fast fade
        this.wobble = 0;
        this.wobbleSpeed = 0;

    } else if (type === 'trail') {
        // Gentle pixie dust
        this.size = Math.random() * 3 + 1; // Trail: 1-4px (was 2-6px)
        this.speedX = (Math.random() - 0.5) * 1;
        this.speedY = (Math.random() - 0.5) * 1;
        this.opacity = 0.8;
        this.decay = 0.05; // Very fast fade
        this.wobble = 0;
        this.wobbleSpeed = 0;
    }
  }

  update() {
    this.x += this.speedX;
    this.y += this.speedY;

    if (this.type === 'rain') {
        this.x += Math.sin(this.wobble) * 0.5;
        this.wobble += this.wobbleSpeed;

        // Reset if off-screen (bottom)
        if (this.y > window.innerHeight + 50) {
            this.y = -50;
            this.x = Math.random() * window.innerWidth;
        }
        // Wrap horizontal
        if (this.x > window.innerWidth + 50) this.x = -50;
        if (this.x < -50) this.x = window.innerWidth + 50;

    } else {
        // Blast and Trail logic: apply decay and gravity
        this.opacity -= this.decay;
        
        if (this.type === 'blast') {
            this.speedY += 0.1; // Gravity
            this.speedX *= 0.98; // Air resistance
        }

        if (this.opacity <= 0) {
            this.isAlive = false;
        }
    }
  }

  draw(ctx: CanvasRenderingContext2D) {
    if (this.opacity <= 0) return;

    ctx.save();
    ctx.globalAlpha = this.opacity;
    
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    
    if (this.type === 'rain' && Math.random() > 0.8) {
        // Some rain particles use gradient
         const gradient = ctx.createRadialGradient(
            this.x, this.y, 0,
            this.x, this.y, this.size
          );
          gradient.addColorStop(0, this.color);
          gradient.addColorStop(1, 'transparent');
          ctx.fillStyle = gradient;
    } else {
        ctx.fillStyle = this.color;
    }
    
    ctx.fill();
    ctx.restore();
  }
}
